import { useApp } from './AppContext.jsx'

import { FETCH_STATUS } from '../shared/constants/fetchStatus.js'

import { useAppRootVars } from './hooks/useAppRootVars.js'

import { Loading } from '../shared/ui/Loading'

/**
 * Application shell component.
 *
 * Renders the root div of the application and handles the top-level
 * loading, error, and ready states based on the configuration status.
 *
 * Responsibilities:
 * - Attaches `containerRef` for layout measurements (ResizeObserver anchor, CSS @container root).
 * - Injects CSS custom properties for theming (`--bim-theme-*`) and layout (`--bim-section-padding`, `--bim-base`).
 * - Displays the loading spinner and error messages.
 * - Renders the main application runtime (`children`) when the configuration is ready.
 * - Provides a portal host for modals and other floating UI elements.
 * @param {object} props
 * @param {React.ReactNode} props.children - The main application content to render when ready.
 */
export function AppShell({ children }) {
  useAppRootVars()

  const {
    containerRef,
    appProjectId,
    appRootId,
    portalHostId,
    configStatus,
    configError,
    logoSrc,
  } = useApp()

  return (
    <div
      id={appRootId}
      ref={containerRef}
      className="bim-app-root"
      data-app="bim-cadaplus-app"
      data-project-id={appProjectId ?? undefined}
    >
      {configStatus === FETCH_STATUS.LOADING && <AppLoading logoSrc={logoSrc} />}
      {configStatus === FETCH_STATUS.ERROR && <AppConfigError error={configError} logoSrc={logoSrc} />}
      {configStatus === FETCH_STATUS.READY && children}
      <div id={portalHostId} data-app-portals="bim-cadaplus-app" />
    </div>
  )
}

function AppLoading({ logoSrc }) {
  return <Loading logoSrc={logoSrc} />
}

function AppConfigError({ error, logoSrc }) {
  return (
    <div className="bim-app-error">
      <Loading logoSrc={logoSrc} />
      {import.meta.env.DEV && error && (
        <pre style={{ padding: '0 1rem 1rem', color: 'red', fontSize: '12px' }}>{error}</pre>
      )}
    </div>
  )
}